import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Location, LocationCreateInput, LocationUpdateInput, MemberLocationsUpdateInput,
} from '@wrapforce/shared';
import { workspacePath } from '@wrapforce/shared/api-client';
import { api } from '@/lib/api';

export function useLocations(workspaceId: string | null) {
  return useQuery({
    queryKey: ['locations', workspaceId],
    queryFn: () => api.get<{ items: Location[] }>(workspacePath(workspaceId!, '/locations')),
    enabled: !!workspaceId,
  });
}

function useInvalidateLocations(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['locations', workspaceId] });
}

export function useCreateLocation(workspaceId: string | null) {
  const invalidate = useInvalidateLocations(workspaceId);
  return useMutation({
    mutationFn: (input: LocationCreateInput) => api.post<Location>(workspacePath(workspaceId!, '/locations'), input),
    onSuccess: invalidate,
  });
}

export function useUpdateLocation(workspaceId: string | null) {
  const invalidate = useInvalidateLocations(workspaceId);
  return useMutation({
    mutationFn: ({ locationId, input }: { locationId: string; input: LocationUpdateInput }) =>
      api.patch<Location>(workspacePath(workspaceId!, `/locations/${locationId}`), input),
    onSuccess: invalidate,
  });
}

export function useDeleteLocation(workspaceId: string | null) {
  const invalidate = useInvalidateLocations(workspaceId);
  return useMutation({
    mutationFn: (locationId: string) => api.delete(workspacePath(workspaceId!, `/locations/${locationId}`)),
    onSuccess: invalidate,
  });
}

// Empty locationIds means access to all locations. Invalidates the exact
// members list key used by WorkspacePage/workspace/api.ts (`[workspaceId, 'members']`).
export function useUpdateMemberLocations(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, input }: { userId: string; input: MemberLocationsUpdateInput }) =>
      api.put(workspacePath(workspaceId!, `/members/${userId}/locations`), input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [workspaceId, 'members'] }),
  });
}
